<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SearchAPI from 'dashboard/api/search';
import MessageAPI from 'dashboard/api/inbox/message';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  message: { type: Object, required: true },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

const query = ref('');
const results = ref([]);
const selectedId = ref(null);
const isSearching = ref(false);
const isSubmitting = ref(false);
let searchTimer = null;

const conversationId = computed(
  () => props.message.conversation_id ?? props.message.conversationId
);
const messageId = computed(() => props.message.id);

const filteredResults = computed(() =>
  results.value.filter(item => Number(item.id) !== Number(conversationId.value))
);

watch(
  () => props.show,
  value => {
    if (!value) {
      query.value = '';
      results.value = [];
      selectedId.value = null;
      isSubmitting.value = false;
    }
  }
);

function onClose() {
  if (isSubmitting.value) return;
  emit('close');
}

async function runSearch() {
  const q = query.value.trim();
  if (!q) {
    results.value = [];
    return;
  }
  isSearching.value = true;
  try {
    const { data } = await SearchAPI.conversations({ q, page: 1 });
    results.value = data?.payload?.conversations || [];
  } catch (error) {
    results.value = [];
  } finally {
    isSearching.value = false;
  }
}

function onQueryInput() {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(runSearch, 300);
}

function selectConversation(item) {
  selectedId.value = item.id;
}

async function confirmForward() {
  if (!selectedId.value || isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    await MessageAPI.forward({
      conversationId: conversationId.value,
      messageId: messageId.value,
      targetConversationId: selectedId.value,
    });
    useAlert(
      t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.SUCCESS', {
        id: selectedId.value,
      })
    );
    emit('close');
  } catch (error) {
    const apiError =
      error?.response?.data?.error ||
      t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.ERROR');
    useAlert(apiError);
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <div class="p-6 flex flex-col gap-4 min-w-[20rem] max-w-lg">
    <div>
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.TITLE') }}
      </h3>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.HINT') }}
      </p>
    </div>

    <input
      v-model="query"
      type="search"
      class="w-full p-2 text-sm border rounded-lg outline-none border-n-weak bg-n-alpha-3 text-n-slate-12"
      :placeholder="
        $t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.SEARCH_PLACEHOLDER')
      "
      :disabled="isSubmitting"
      @input="onQueryInput"
    />

    <div
      class="max-h-64 overflow-y-auto border rounded-lg border-n-weak divide-y divide-n-weak"
    >
      <p v-if="isSearching" class="p-3 text-sm text-n-slate-11">
        {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.SEARCHING') }}
      </p>
      <p
        v-else-if="query.trim() && !filteredResults.length"
        class="p-3 text-sm text-n-slate-11"
      >
        {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.EMPTY') }}
      </p>
      <p v-else-if="!query.trim()" class="p-3 text-sm text-n-slate-11">
        {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.TYPE_TO_SEARCH') }}
      </p>
      <button
        v-for="item in filteredResults"
        :key="item.id"
        type="button"
        class="w-full text-left p-3 hover:bg-n-alpha-2 transition-colors"
        :class="{ 'bg-n-alpha-3': selectedId === item.id }"
        :disabled="isSubmitting"
        @click="selectConversation(item)"
      >
        <div class="text-sm font-medium text-n-slate-12">
          #{{ item.id }}
          <span v-if="item.contact?.name"> — {{ item.contact.name }} </span>
        </div>
        <div class="text-xs text-n-slate-11 truncate">
          {{ item.contact?.phone_number || item.contact?.email || '' }}
          <span v-if="item.inbox?.name"> · {{ item.inbox.name }}</span>
        </div>
      </button>
    </div>

    <div class="flex justify-end gap-2">
      <NextButton
        sm
        slate
        :label="$t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.CANCEL')"
        :disabled="isSubmitting"
        @click="onClose"
      />
      <NextButton
        sm
        :label="$t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.CONFIRM')"
        :disabled="!selectedId || isSubmitting"
        :is-loading="isSubmitting"
        @click="confirmForward"
      />
    </div>
  </div>
</template>
